/**
 * WorldRoad - Flush dirt road for continuous travel
 * 
 * A simple road that sits flush with the terrain (not raised).
 * Uses a thin plane with road texture/color.
 */

import { useMemo } from 'react';
import { RigidBody, CuboidCollider } from '@react-three/rapier';

export default function WorldRoad({ 
  from = { x: 0, z: 0 }, 
  to = { x: 0, z: -50 },
  width = 4,
  color = '#5c4a3a'  // Dirt brown
}) {
  // Calculate road geometry
  const roadGeometry = useMemo(() => {
    const dx = to.x - from.x;
    const dz = to.z - from.z;
    const length = Math.sqrt(dx * dx + dz * dz);
    const angle = Math.atan2(dx, dz);
    
    return { length, angle };
  }, [from, to]);

  const centerX = (from.x + to.x) / 2;
  const centerZ = (from.z + to.z) / 2;

  return (
    <group position={[centerX, 0.02, centerZ]} rotation={[0, roadGeometry.angle, 0]}>
      {/* Road physics collider - thin but walkable, backup in case of terrain gaps */}
      <RigidBody type="fixed" colliders={false}>
        <CuboidCollider 
          args={[width / 2, 0.05, roadGeometry.length / 2]} 
          position={[0, -0.04, 0]} 
        />
      </RigidBody>
      
      {/* Main road surface - flush with ground */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <planeGeometry args={[width, roadGeometry.length]} />
        <meshStandardMaterial 
          color={color}
          roughness={0.95}
          metalness={0}
        />
      </mesh>
      
      {/* Road edge markings (subtle) - slightly raised above main road surface */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[width / 2 - 0.1, 0.005, 0]}>
        <planeGeometry args={[0.15, roadGeometry.length]} />
        <meshStandardMaterial 
          color="#4a3f32"
          roughness={1}
          metalness={0}
          polygonOffset
          polygonOffsetFactor={-1}
        />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[-width / 2 + 0.1, 0.005, 0]}>
        <planeGeometry args={[0.15, roadGeometry.length]} />
        <meshStandardMaterial 
          color="#4a3f32"
          roughness={1}
          metalness={0}
          polygonOffset
          polygonOffsetFactor={-1}
        />
      </mesh>
    </group>
  );
}

/**
 * TownGate - Simple gate structure for Thornhaven entrance
 * Has collision on pillars only - center is open for walking through
 */
export function TownGate({ position = [0, 0, 0], rotation = 0 }) {
  return (
    <group position={position} rotation={[0, rotation, 0]}>
      {/* Left pillar with collision */}
      <RigidBody type="fixed" colliders={false} position={[-3, 0, 0]}>
        <CuboidCollider args={[0.6, 2, 0.6]} position={[0, 2, 0]} />
        <mesh position={[0, 2, 0]} castShadow receiveShadow>
          <boxGeometry args={[1.2, 4, 1.2]} />
          <meshStandardMaterial color="#6b5b4a" roughness={0.8} />
        </mesh>
      </RigidBody>
      
      {/* Right pillar with collision */}
      <RigidBody type="fixed" colliders={false} position={[3, 0, 0]}>
        <CuboidCollider args={[0.6, 2, 0.6]} position={[0, 2, 0]} />
        <mesh position={[0, 2, 0]} castShadow receiveShadow>
          <boxGeometry args={[1.2, 4, 1.2]} />
          <meshStandardMaterial color="#6b5b4a" roughness={0.8} />
        </mesh>
      </RigidBody>
      
      {/* Top beam - no collision needed (above player) */}
      <mesh position={[0, 4.3, 0]} castShadow receiveShadow>
        <boxGeometry args={[7.5, 0.6, 1]} />
        <meshStandardMaterial color="#5a4a3a" roughness={0.75} />
      </mesh>
      
      {/* Decorative top */}
      <mesh position={[0, 4.8, 0]} castShadow>
        <boxGeometry args={[8, 0.3, 1.2]} />
        <meshStandardMaterial color="#4a3a2a" roughness={0.8} />
      </mesh>
    </group>
  );
}

/**
 * FrostGate - Northern-style gate for Frostholm entrance
 * Larger and more imposing, with snow-covered elements
 */
export function FrostGate({ position = [0, 0, 0], rotation = 0 }) {
  return (
    <group position={position} rotation={[0, rotation, 0]}>
      {/* Left pillar - stone base with wooden top */}
      <RigidBody type="fixed" colliders={false} position={[-4, 0, 0]}>
        <CuboidCollider args={[0.8, 3, 0.8]} position={[0, 3, 0]} />
        {/* Stone base */}
        <mesh position={[0, 1.5, 0]} castShadow receiveShadow>
          <boxGeometry args={[1.8, 3, 1.8]} />
          <meshStandardMaterial color="#5a5a5a" roughness={0.9} />
        </mesh>
        {/* Wooden top */}
        <mesh position={[0, 4, 0]} castShadow receiveShadow>
          <boxGeometry args={[1.4, 2, 1.4]} />
          <meshStandardMaterial color="#5a4a3a" roughness={0.85} />
        </mesh>
        {/* Snow cap */}
        <mesh position={[0, 5.2, 0]} castShadow>
          <boxGeometry args={[1.6, 0.3, 1.6]} />
          <meshStandardMaterial color="#e0e0e0" roughness={0.7} />
        </mesh>
      </RigidBody>
      
      {/* Right pillar */}
      <RigidBody type="fixed" colliders={false} position={[4, 0, 0]}>
        <CuboidCollider args={[0.8, 3, 0.8]} position={[0, 3, 0]} />
        <mesh position={[0, 1.5, 0]} castShadow receiveShadow>
          <boxGeometry args={[1.8, 3, 1.8]} />
          <meshStandardMaterial color="#5a5a5a" roughness={0.9} />
        </mesh>
        <mesh position={[0, 4, 0]} castShadow receiveShadow>
          <boxGeometry args={[1.4, 2, 1.4]} />
          <meshStandardMaterial color="#5a4a3a" roughness={0.85} />
        </mesh>
        <mesh position={[0, 5.2, 0]} castShadow>
          <boxGeometry args={[1.6, 0.3, 1.6]} />
          <meshStandardMaterial color="#e0e0e0" roughness={0.7} />
        </mesh>
      </RigidBody>
      
      {/* Top beam - heavy timber */}
      <mesh position={[0, 5.5, 0]} castShadow receiveShadow>
        <boxGeometry args={[10, 0.8, 1.2]} />
        <meshStandardMaterial color="#4a3a2a" roughness={0.8} />
      </mesh>
      
      {/* Snow on beam */}
      <mesh position={[0, 6, 0]} castShadow>
        <boxGeometry args={[10.5, 0.2, 1.4]} />
        <meshStandardMaterial color="#e8e8e8" roughness={0.7} />
      </mesh>
      
      {/* Decorative runes/carvings (simplified as markings) */}
      <mesh position={[0, 5.5, 0.65]} castShadow>
        <boxGeometry args={[6, 0.4, 0.05]} />
        <meshStandardMaterial color="#3a2a1a" roughness={0.9} />
      </mesh>
    </group>
  );
}

/**
 * StoneBridge - Stone bridge over river crossing
 */
export function StoneBridge({ position = [0, 0, 0] }) {
  return (
    <group position={position}>
      {/* Main bridge surface - walkable */}
      <RigidBody type="fixed" colliders={false}>
        <CuboidCollider args={[3, 0.3, 5]} position={[0, 0.3, 0]} />
      </RigidBody>
      
      {/* Bridge deck */}
      <mesh position={[0, 0.3, 0]} castShadow receiveShadow>
        <boxGeometry args={[6, 0.6, 10]} />
        <meshStandardMaterial color="#6a6a6a" roughness={0.9} />
      </mesh>
      
      {/* Left railing base */}
      <mesh position={[-2.7, 0.8, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.4, 1, 10]} />
        <meshStandardMaterial color="#5a5a5a" roughness={0.9} />
      </mesh>
      
      {/* Right railing base */}
      <mesh position={[2.7, 0.8, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.4, 1, 10]} />
        <meshStandardMaterial color="#5a5a5a" roughness={0.9} />
      </mesh>
      
      {/* Support pillars (under bridge, visual only) */}
      <mesh position={[-2, -0.8, 0]} castShadow>
        <boxGeometry args={[1, 2, 1.5]} />
        <meshStandardMaterial color="#505050" roughness={0.95} />
      </mesh>
      <mesh position={[2, -0.8, 0]} castShadow>
        <boxGeometry args={[1, 2, 1.5]} />
        <meshStandardMaterial color="#505050" roughness={0.95} />
      </mesh>
    </group>
  );
}
